import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from 'react-router'
import { GoogleOAuthProvider } from '@react-oauth/google'

import { Toaster } from '~/components/ui/sonner'
import { AuthProvider } from '~/context/auth'
import { FederationsProvider } from '~/context/federations'
import { UnionsProvider } from '~/context/unions'
import type { Route } from './+types/root'
import './app.css'

// Polyfill crypto.subtle for HTTP contexts (non-HTTPS production)
if (typeof window !== 'undefined' && window.crypto && !window.crypto.subtle) {
  console.warn('crypto.subtle not available - adding polyfill for HTTP context');
  
  // Basic polyfill for MSAL cryptographic requirements
  (window.crypto as any).subtle = {
    digest: async (algorithm: string, data: BufferSource) => {
      // Fallback implementation for digest operations
      const array = new Uint8Array(data as ArrayBuffer);
      let hash = 0;
      for (let i = 0; i < array.length; i++) {
        hash = ((hash << 5) - hash) + array[i];
        hash = hash & hash;
      }
      const buffer = new ArrayBuffer(32);
      const view = new DataView(buffer);
      view.setUint32(0, hash, true);
      return buffer;
    },
  };
  
  // Ensure getRandomValues exists
  if (!window.crypto.getRandomValues) {
    window.crypto.getRandomValues = ((array: any) => {
      for (let i = 0; i < array.length; i++) {
        array[i] = Math.floor(Math.random() * 256);
      }
      return array;
    }) as any;
  }
}

export const links: Route.LinksFunction = () => [
  { rel: 'icon', href: '/logo_guia.png', type: 'image/png' },
]

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <Toaster position="top-right" richColors />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <AuthProvider>
        <FederationsProvider>
          <UnionsProvider>
            <Outlet />
          </UnionsProvider>
        </FederationsProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  )
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!'
  let details = 'An unexpected error occurred.'
  let stack: string | undefined

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error'
    details =
      error.status === 404
        ? 'The requested page could not be found.'
        : error.statusText || details
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message
    stack = error.stack
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  )
}
